import React, {useContext, createContext} from 'react';
import type {ClientInfoQuery} from './net/types/graphql';

import {useClientInfo} from './net/query/ClientInfo.gql';

export type ClientInfo = Extract<
  ClientInfoQuery['node'],
  {__typename?: 'ClientAccount'}
>;

export type FSM = ClientInfoQuery['fsm'];

export type FeatureAssignment = NonNullable<
  ClientInfoQuery['featureAssignments']
>[number];

export type ClientInfoQueryResult = {
  loading: boolean;
  error: Error | null;
  clientInfo: ClientInfo | null;
  fsm: FSM | null;
  featureAssignments: Readonly<FeatureAssignment[]> | null;
};

const ClientInfoContext = createContext<ClientInfoQueryResult>({
  loading: false,
  error: null,
  clientInfo: null,
  fsm: null,
  featureAssignments: null,
});

export const useClientInfoCtx = () => {
  const ci = useContext(ClientInfoContext);

  return ci;
};

export type ClientInfoProviderProps = {
  children: React.ReactNode;
  accountId: string;
  admin: boolean;
};

export const ClientInfoProvider = ({
  children,
  accountId,
  admin,
}: ClientInfoProviderProps) => {
  const {
    data,
    loading,
    error: netError,
  } = useClientInfo({
    pollInterval: 60000,
    variables: {
      id: accountId!,
      idAsStr: accountId!,
      admin,
    },
  });

  let clientInfo: ClientInfo | null = null;
  let error = netError ? new Error(netError.message) : null;
  if (!error) {
    if (!loading) {
      if (data?.node?.__typename !== 'ClientAccount') {
        error = new Error('error loading client info');
        clientInfo = null;
      } else {
        clientInfo = data.node;
      }
    }
  }

  const d = {
    loading,
    error,
    clientInfo,
    fsm: data?.fsm || null,
    featureAssignments: data?.featureAssignments || null,
  };

  return (
    <ClientInfoContext.Provider value={d}>
      {children}
    </ClientInfoContext.Provider>
  );
};
